<template>
  <div class="provider-lists">
    <transition name="view-fade" mode="out-in">
      <!-- Provider List View -->
      <div
        v-if="viewState === 'providers'"
        key="providers"
        class="providers-view"
      >
        <!-- Providers Header -->
        <div class="content-header">
          <div class="header-row">
            <BreadcrumbNav
              :items="providersBreadcrumb"
              @breadcrumb-click="handleBreadcrumbClick"
            />
            <div class="search-section">
              <div
                class="search-container"
                :class="{ 'search-expanded': searchExpanded }"
              >
                <button class="search-toggle-btn" @click="toggleSearch">
                  {{ searchExpanded ? '✕' : '🔍' }}
                </button>
                <div v-if="searchExpanded" class="search-input-wrapper">
                  <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="search-input"
                    placeholder="ค้นหาผู้ให้บริการ..."
                    @input="onSearchInput"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="provider-grid">
          <div
            v-for="provider in filteredProviders"
            :key="provider.id"
            class="provider-card"
            :class="`provider-tier-${provider.providerTier}`"
            @click="onProviderClick(provider)"
          >
            <div class="provider-logo">
              <img
                :src="provider.logoTransparentURL"
                :alt="provider.providerName"
                class="logo-image"
              />
            </div>
            <div class="provider-name">
              {{ provider.providerName }}
            </div>
          </div>
        </div>
      </div>

      <!-- Provider Games View -->
      <div v-else-if="viewState === 'games'" key="games" class="games-view">
        <!-- Games Header -->
        <GameGridContent
          :games="filteredProviderGames"
          :loading="loading"
          :show-online-count="true"
          :breadcrumb-items="gamesBreadcrumbItems"
          :show-back-button="true"
          :show-percentage="true"
          :back-button-text="'← กลับ'"
          @game-select="handleGameSelect"
          @show-login-modal="$emit('show-login-modal')"
          @breadcrumb-click="handleBreadcrumbClick"
          @back-click="backToProviders"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { gameAPI } from '@/services/gameAPI'
import GameGridContent from './GameGridContent.vue'
import BreadcrumbNav from './BreadcrumbNav.vue'

export default {
  name: 'ProviderLists',
  components: {
    GameGridContent,
    BreadcrumbNav
  },
  props: {
    providers: {
      type: Array,
      default: () => []
    },
    sectionTitle: {
      type: String,
      default: 'ผู้ให้บริการ'
    }
  },
  computed: {
    filteredProviders() {
      if (!this.searchQuery.trim()) {
        return this.providers
      }
      const query = this.searchQuery.toLowerCase()
      return this.providers.filter(provider =>
        provider.providerName.toLowerCase().includes(query)
      )
    },
    filteredProviderGames() {
      if (!this.searchQuery.trim()) {
        return this.providerGames
      }
      const query = this.searchQuery.toLowerCase()
      return this.providerGames.filter(
        game => game.gameName && game.gameName.toLowerCase().includes(query)
      )
    },

    // Breadcrumb navigation
    providersBreadcrumb() {
      return [
        {
          label: 'หน้าหลัก',
          clickable: true,
          action: 'home'
        },
        {
          label: `${this.sectionTitle} [${this.providers.length}] ค่าย`,
          clickable: false
        }
      ]
    },

    // Custom breadcrumb for games view when passed to GameGridContent
    gamesBreadcrumbItems() {
      return [
        {
          label: 'หน้าหลัก',
          clickable: true,
          action: 'home'
        },
        {
          label: `${this.sectionTitle} [${this.providers.length}] ค่าย`,
          clickable: true,
          action: 'providers'
        },
        {
          label: this.selectedProvider
            ? this.selectedProvider.providerName
            : '',
          clickable: false
        }
      ]
    }
  },
  data() {
    return {
      viewState: 'providers', // 'providers' | 'games'
      selectedProvider: null,
      providerGames: [],
      loading: false,
      // Internal search state
      searchExpanded: false,
      searchQuery: ''
    }
  },
  methods: {
    async onProviderClick(provider) {
      this.loading = true
      this.selectedProvider = provider
      this.viewState = 'games'
      this.$emit('view-state-change', 'games')

      // Scroll to top after view change for better UX
      this.$nextTick(() => {
        const appElement = document.querySelector('#app')
        if (appElement) {
          appElement.scrollTo({ top: 80, behavior: 'smooth' })
        }
      })

      try {
        // Fetch games from real API for this provider
        const response = await gameAPI.getProviderGames(
          provider.provider,
          provider.providerType
        )
        console.log('Provider games response:', response)

        if (response.success && response.data) {
          // Handle nested data structure if needed (similar to slots providers)
          this.providerGames = response.data.result || response.data || []
          console.log('Loaded provider games:', this.providerGames.length)
        } else {
          console.error('Invalid response format from provider games API')
          this.providerGames = []
        }
      } catch (error) {
        console.error('Failed to fetch provider games:', error)
        this.providerGames = []
      } finally {
        this.loading = false
      }

      // Emit view state change after loading completes
      this.$emit('view-state-change', 'games')

      // Still emit for parent component if needed
      this.$emit('provider-selected', provider)
    },

    backToProviders() {
      this.viewState = 'providers'
      this.selectedProvider = null
      this.providerGames = []
      this.$emit('view-state-change', 'providers')

      // Scroll to top when returning to providers view
      this.$nextTick(() => {
        const appElement = document.querySelector('#app')
        if (appElement) {
          appElement.scrollTo({ top: 80, behavior: 'smooth' })
        }
      })
    },

    handleGameSelect(game) {
      this.$emit('game-select', game)
    },

    // Search methods
    toggleSearch() {
      if (this.searchExpanded) {
        this.closeSearch()
      } else {
        this.searchExpanded = true
        this.$nextTick(() => {
          const { searchInput } = this.$refs
          if (searchInput) {
            searchInput.focus()
          }
        })
      }
    },

    closeSearch() {
      this.searchExpanded = false
      this.searchQuery = ''
    },

    onSearchInput(event) {
      this.searchQuery = event.target.value
    },

    // Breadcrumb navigation
    handleBreadcrumbClick(action) {
      if (action === 'home') {
        this.$emit('navigate-home')
      } else if (action === 'providers') {
        this.backToProviders()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.provider-lists {
  width: 100%;
  padding: 0;
}

// Content header styles
.content-header {
  padding: 0 10px 15px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    padding: 0 15px 15px;
  }
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 15px;
  }
}

.content-title {
  color: #fff;
  font-size: 1.4rem;
  font-weight: 500;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
}

.provider-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(8px, 2vw, 16px);
  width: 100%;

  // Responsive breakpoints
  @media (min-width: 576px) {
    gap: clamp(12px, 2.5vw, 20px);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: clamp(16px, 3vw, 24px);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(5, 1fr);
    gap: clamp(20px, 3.5vw, 28px);
  }
}

.provider-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: clamp(8px, 2vw, 12px);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(212, 175, 55, 0.2);
  background-color: rgba(34, 33, 34, 0.807);
  padding: clamp(8px, 2vw, 12px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
    border-color: rgba(212, 175, 55, 0.5);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.4);
  }
}

.provider-logo {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(4px, 1vw, 8px);

  .logo-image {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
}

.provider-name {
  color: #ffffff;
  font-size: clamp(10px, 2vw, 14px);
  font-weight: 300;
  text-align: center;
  line-height: 1.2;
  margin-top: auto;
}

// Provider tier styling
.provider-tier-vvip {
  border-color: rgba(255, 215, 0, 0.4);

  &:hover {
    border-color: rgba(255, 215, 0, 0.7);
    box-shadow: 0 8px 20px rgba(255, 215, 0, 0.3);
  }
}

.provider-tier-vip {
  border-color: rgba(138, 43, 226, 0.4);

  &:hover {
    border-color: rgba(138, 43, 226, 0.7);
    box-shadow: 0 8px 20px rgba(138, 43, 226, 0.3);
  }
}

.provider-tier-exclusive {
  border-color: rgba(220, 20, 60, 0.4);

  &:hover {
    border-color: rgba(220, 20, 60, 0.7);
    box-shadow: 0 8px 20px rgba(220, 20, 60, 0.3);
  }
}

// Mobile optimizations
@media (max-width: 480px) {
  .provider-grid {
    gap: clamp(6px, 1.5vw, 12px);
  }

  .provider-card {
    border-radius: clamp(6px, 1.5vw, 10px);
    padding: clamp(6px, 1.5vw, 10px);

    &:hover {
      transform: translateY(-1px);
    }
  }

  .provider-logo {
    margin-bottom: clamp(3px, 0.8vw, 6px);
  }

  .provider-name {
    font-size: clamp(12px, 2.2vw, 12px);
  }
}

// Extra small devices
@media (max-width: 360px) {
  .provider-card {
    padding: clamp(4px, 1.2vw, 8px);
  }

  .provider-logo {
    margin-bottom: clamp(2px, 0.6vw, 4px);
  }

  .provider-name {
    font-size: clamp(10px, 2.5vw, 11px);
  }
}

// Search styles
.search-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
}

.provider-title {
  color: #fff;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
}

// View transitions
.view-fade-enter-active,
.view-fade-leave-active {
  transition: all 0.3s ease;
}

.view-fade-enter-from,
.view-fade-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

@media (max-width: 768px) {
  .provider-title {
    font-size: 1.1rem;
  }
}

// Search styles
.search-header {
  margin-bottom: 15px;
  padding: 0 10px;
}

.search-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.search-container {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;

  &:not(.search-expanded) {
    width: 40px;
  }

  &.search-expanded {
    width: 250px;

    @media (max-width: 768px) {
      width: 200px;
    }

    @media (max-width: 480px) {
      width: 180px;
    }
  }
}

.search-toggle-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 20px;
  background: rgba(209, 55, 212, 0.2);
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    background: rgba(209, 55, 212, 0.3);
    transform: scale(1.05);
  }
}

.search-input-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  overflow: hidden;
  animation: expandSearch 0.3s ease;
  z-index: 1;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 15px 0 45px;
  font-size: 14px;
  background: transparent;
  color: #333;

  &::placeholder {
    color: #999;
  }
}

@media (max-width: 768px) {
  .search-header {
    padding: 0 15px;
    margin-bottom: 10px;
  }
}

@keyframes expandSearch {
  from {
    opacity: 0;
    transform: scaleX(0);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
